/**
 * CSS Manager for MFE stylesheet lifecycle management.
 *
 * Responsible for injecting, removing, and tracking <link> tags
 * for MFE CSS bundles. Provides graceful degradation on load
 * failure or timeout.
 */

export interface CSSManagerOptions {
  /** Timeout in ms before allowing render without CSS. Default: 5000 */
  timeout?: number;
  /** Callback when CSS load fails */
  onError?: (mfeName: string, error: Error) => void;
}

export interface CSSManager {
  /**
   * Injects a <link> tag for the MFE's CSS bundle.
   * Resolves when CSS is loaded or times out.
   * If the link already exists, resolves immediately.
   */
  inject(mfeName: string, cssUrl: string): Promise<void>;

  /**
   * Removes the <link> tag for the given MFE.
   * No-op if the tag doesn't exist.
   */
  remove(mfeName: string): void;

  /**
   * Returns true if the MFE's CSS link tag is currently in <head>.
   */
  isLoaded(mfeName: string): boolean;

  /**
   * Returns the <link> element ID for a given MFE name.
   */
  getLinkId(mfeName: string): string;
}

const DEFAULT_TIMEOUT = 5000;

/**
 * Creates a CSS Manager instance for managing MFE stylesheet lifecycle.
 */
export function createCSSManager(options?: CSSManagerOptions): CSSManager {
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
  const onError = options?.onError;

  function getLinkId(mfeName: string): string {
    return `mfe-css-${mfeName}`;
  }

  function isLoaded(mfeName: string): boolean {
    const linkId = getLinkId(mfeName);
    return document.getElementById(linkId) !== null;
  }

  function inject(mfeName: string, cssUrl: string): Promise<void> {
    return new Promise<void>((resolve) => {
      const linkId = getLinkId(mfeName);

      // Idempotent: if link already exists, resolve immediately
      if (document.getElementById(linkId)) {
        resolve();
        return;
      }

      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.href = cssUrl;

      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let settled = false;

      const cleanup = () => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      const settle = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve();
      };

      link.onload = () => {
        settle();
      };

      link.onerror = () => {
        const error = new Error(
          `Failed to load CSS for MFE "${mfeName}" from ${cssUrl}`,
        );
        console.warn(
          `[CSSManager] CSS load failed for "${mfeName}": ${cssUrl}`,
        );
        if (onError) {
          onError(mfeName, error);
        }
        settle();
      };

      // Timeout: graceful degradation — resolve and allow render
      timeoutId = setTimeout(() => {
        if (!settled) {
          console.warn(
            `[CSSManager] CSS load timed out for "${mfeName}" after ${timeout}ms: ${cssUrl}`,
          );
          const error = new Error(
            `CSS load timed out for MFE "${mfeName}" after ${timeout}ms`,
          );
          if (onError) {
            onError(mfeName, error);
          }
          settle();
        }
      }, timeout);

      document.head.appendChild(link);
    });
  }

  function remove(mfeName: string): void {
    const linkId = getLinkId(mfeName);
    const existing = document.getElementById(linkId);
    if (existing) {
      existing.remove();
    }
  }

  return {
    inject,
    remove,
    isLoaded,
    getLinkId,
  };
}
